import { Injectable } from "@angular/core";
import { AssetClass, BlotterType, OpsExceptionType } from "shared/cubiq-model-multi-asset";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  CcyLookupService,
  CcyPairLookupService,
  ColumnFilterType,
  IndexType,
  OpsIntEventPrivatizer,
  OpsIntLookupService,
  ProfilePromiseActionService,
} from "..";

import { BaseTableDefinitionFactory } from "./BaseTableDefinitionFactory";
import { BfxColDef, TableDefinition } from "./TableDefinition";
import { DIReasonHelper } from "./orders/DIReasonHelper";
import { TranslateService } from "@ngx-translate/core";
import { Optional } from "@angular/core";
import { TreeMultiMap } from "jstreemap";

const WRAP_TEXT_CELL_CLASS: string = "ag-cell-wrap-ellipsis";
const EMPTY_CATEGORY_LABEL = "General";

@Injectable()
export class OpsIntTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected diReasonHelper: DIReasonHelper;
  protected localeString: string = "DD-MMM-YY HH:mm:ss";
  protected defaultSortModel: any = [{ colId: "time", sort: "desc" }];
  protected static typeOrdinalSort: Map<CubiqTS.OpsExceptionType, number> = null;

  constructor(
    profileService: ProfilePromiseActionService,
    @Optional() protected _translate: TranslateService,
    @Optional() _ccyLookupService: CcyLookupService,
    @Optional() _ccyPairLookupService: CcyPairLookupService,
    @Optional() protected _opsIntLookupService: OpsIntLookupService,
  ) {
    super(profileService);

    this.diReasonHelper = new DIReasonHelper(_translate, _ccyLookupService);

    if (OpsIntTableDefinitionFactory.typeOrdinalSort == null) {
      this.initTypeOrdinalSortMap(_translate);
    }
  }

  protected initTypeOrdinalSortMap(translate: TranslateService): void {
    // several enum types distill down to simpler human readable name
    // sort should apply to these names. calculate an ordinal value and place in a lookup map
    let typeTreeMap = new TreeMultiMap<string, any>();
    typeTreeMap.compareFunc = (a: string, b: string) => {
      let cmp = a.localeCompare(b);

      if (cmp == 0) {
        return 0;
      } else if (cmp > 0) {
        return 1;
      } else if (cmp < 0) {
        return -1;
      }
    };
    let typeOrdinalSort = new Map();

    CubiqTS.OpsExceptionType.getAllElements().forEach((type) => {
      // tree map handles base sort of string automatically
      const translation = translate.instant(`OpsExceptionType.${CubiqTS.OpsExceptionType[type]}`);
      typeTreeMap.insertMulti(translation, type);
    });

    let index = -1;
    let lastKey = null;
    for (let [key, value] of typeTreeMap.entries()) {
      if (key != lastKey) {
        lastKey = key;
        index++;
      }

      // many types have the same sort value, due to shared translation
      typeOrdinalSort.set(value, index);
    }

    OpsIntTableDefinitionFactory.typeOrdinalSort = typeOrdinalSort;
  }

  protected getDefaultColumnDef(): any {
    return {
      suppressMenu: true,
      cellClass: [WRAP_TEXT_CELL_CLASS],
      tooltipComponent: "opsIntTooltip",
    };
  }

  public createDef(): TableDefinition {
    super.createDef();
    let td = new TableDefinition("eventId", "as_sort_eventId");

    let selectionColumn: BfxColDef = this.getDefaultColumnDef();
    selectionColumn.field = "selection";
    selectionColumn.sortingOrder = [];
    selectionColumn.pinned = "left";
    selectionColumn.lockPosition = true;
    selectionColumn.checkboxSelection = true;
    selectionColumn.headerName = "(0)";
    selectionColumn.headerCheckboxSelection = true;
    selectionColumn.headerCheckboxSelectionFilteredOnly = true;
    selectionColumn.sortable = false;
    selectionColumn.suppressMovable = true;
    selectionColumn.resizable = true;
    selectionColumn.suppressMenu = true;
    selectionColumn.columnFilterType = ColumnFilterType.NONE;
    selectionColumn.omitFromPicker = true;
    selectionColumn.disableInPicker = true;
    selectionColumn.suppressSizeToFit = true;
    selectionColumn.minWidth = this.threeWs;
    selectionColumn.width = this.fiveWs;
    selectionColumn.maxWidth = this.sixWs;
    selectionColumn.valueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      return "";
    }.bind(this);
    selectionColumn.cellRenderer = function (this: null, params) {
      return "";
    };
    td.addColumn(selectionColumn);

    let categoryColumn: BfxColDef = this.getDefaultColumnDef();
    categoryColumn.field = "category";
    categoryColumn.sortingOrder = ["asc", "desc"];
    categoryColumn.headerName = "Category";
    categoryColumn.headerCheckboxSelection = false;
    categoryColumn.columnFilterType = ColumnFilterType.CATEGORY;

    categoryColumn.minWidth = this.oneWs;
    categoryColumn.width = this.eightWs;
    categoryColumn.maxWidth = this.eightWs;
    categoryColumn.valueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      let categoryId: string = null;
      if (
        (params != null &&
          params.data != null &&
          params.data instanceof CubiqTS.OpsIntEventImpl &&
          params.data.info != null &&
          params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
          params.data.info.opsException != null &&
          params.data.info.opsException instanceof CubiqTS.OpsExcRecordImpl) ||
        (params != null && params instanceof CubiqTS.OpsIntEventImpl)
      ) {
        let paramData = params instanceof CubiqTS.OpsIntEventImpl ? params : params.data;
        let typeId: CubiqTS.OpsExceptionType = paramData.info.opsException.exceptionTypeId;

        categoryId = this._opsIntLookupService.getCategoryIdByExceptionTypeId(typeId);
      }

      return categoryId;
    }.bind(this);
    categoryColumn.indexType = IndexType.AgGridDefault;
    let categoryFormatterFn = function (this: OpsIntTableDefinitionFactory, params) {
      let categoryDesc: string = null;
      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.info != null &&
        params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        params.data.info.opsException != null &&
        params.data.info.opsException instanceof CubiqTS.OpsExcRecordImpl
      ) {
        let typeId: CubiqTS.OpsExceptionType = params.data.info.opsException.exceptionTypeId;

        categoryDesc = this._opsIntLookupService.getCategoryDescByExceptionTypeId(typeId);
      }

      if (categoryDesc == null) {
        return EMPTY_CATEGORY_LABEL;
      }

      return categoryDesc;
    }.bind(this);
    categoryColumn.valueFormatter = categoryFormatterFn;
    categoryColumn.getQuickFilterText = categoryFormatterFn;
    categoryColumn.indexType = IndexType.AgGridDefault;
    categoryColumn.comparator = function (
      this: OpsIntTableDefinitionFactory,
      valueA,
      valueB,
      nodeA,
      nodeB,
    ) {
      let formattedA = valueA != null ? categoryFormatterFn(nodeA) : "";
      let formattedB = valueB != null ? categoryFormatterFn(nodeB) : "";

      if (formattedA == null) {
        formattedA = "";
      }
      if (formattedB == null) {
        formattedB = "";
      }

      return formattedA.localeCompare(formattedB);
    }.bind(this);
    if (!this.restoreFromConfig(categoryColumn)) {
      categoryColumn.position = this.position.next();
    }
    td.addColumn(categoryColumn);

    let timeColumn: BfxColDef = this.getDefaultColumnDef();
    timeColumn.field = "time";
    timeColumn.sortingOrder = ["asc", "desc"];
    timeColumn.headerName = "Time";
    timeColumn.headerCheckboxSelection = false;
    timeColumn.columnFilterType = ColumnFilterType.NONE;
    timeColumn.minWidth = this.oneWs;
    timeColumn.width = this.eightWs;
    timeColumn.maxWidth = this.eightWs;
    timeColumn.valueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      let createdDt = null;
      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.info != null &&
        params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        params.data.info.opsException != null &&
        params.data.info.opsException instanceof CubiqTS.OpsExcRecordImpl &&
        CubiqTS.moment.isMoment(params.data.info.opsException.createdDt)
      ) {
        createdDt = params.data.info.opsException.createdDt;
      }
      return createdDt;
    }.bind(this);
    let timeFormatterFn = function (this: OpsIntTableDefinitionFactory, params) {
      if (params.value != null) {
        return params.value.format(this.localeString);
      }
    }.bind(this);
    timeColumn.valueFormatter = timeFormatterFn;
    timeColumn.getQuickFilterText = timeFormatterFn;

    if (!this.restoreFromConfig(timeColumn)) {
      timeColumn.position = this.position.next();
    }
    td.addColumn(timeColumn);

    let typeColumn: BfxColDef = this.getDefaultColumnDef();
    typeColumn.field = "type";
    typeColumn.sortingOrder = ["asc", "desc"];
    typeColumn.sortable = true;
    typeColumn.headerName = "Type";
    typeColumn.headerCheckboxSelection = false;
    typeColumn.columnFilterType = ColumnFilterType.NONE;
    typeColumn.minWidth = this.oneWs;
    typeColumn.width = this.twelveWs;
    typeColumn.maxWidth = this.fifteenWs;
    typeColumn.valueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      let type: CubiqTS.OpsExceptionType = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.info != null &&
        params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        params.data.info.opsException != null &&
        params.data.info.opsException instanceof CubiqTS.OpsExcRecordImpl
      ) {
        type = params.data.info.opsException.exceptionTypeId;
      }

      return type;
    }.bind(this);
    let typeFormatterFn = function (this: OpsIntTableDefinitionFactory, params) {
      if (params.value != null) {
        let opsExceptionTypeName = CubiqTS.OpsExceptionType[params.value];

        if (opsExceptionTypeName != null) {
          return this._translate.instant(`OpsExceptionType.${opsExceptionTypeName}`);
        }
      }

      return "";
    }.bind(this);
    typeColumn.valueFormatter = typeFormatterFn;
    typeColumn.getQuickFilterText = typeFormatterFn;
    typeColumn.comparator = function (
      this: OpsIntTableDefinitionFactory,
      typeA,
      typeB,
      nodeA,
      nodeB,
      isInverted,
    ) {
      const valueA = OpsIntTableDefinitionFactory.typeOrdinalSort.get(typeA);
      const valueB = OpsIntTableDefinitionFactory.typeOrdinalSort.get(typeB);

      return valueA === valueB ? 0 : valueA > valueB ? 1 : -1;
    }.bind(this);
    if (!this.restoreFromConfig(typeColumn)) {
      typeColumn.position = this.position.next();
    }
    td.addColumn(typeColumn);

    let descriptionColumn: BfxColDef = this.getDefaultColumnDef();
    descriptionColumn.field = "descriptionTxt";
    descriptionColumn.sortingOrder = ["asc", "desc"];
    descriptionColumn.headerName = "Description";
    descriptionColumn.headerCheckboxSelection = false;
    descriptionColumn.columnFilterType = ColumnFilterType.NONE;
    descriptionColumn.minWidth = this.oneWs;
    descriptionColumn.width = this.wrapEllipsisWs;

    descriptionColumn.valueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.info != null &&
        params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        params.data.info.opsException != null &&
        params.data.info.opsException instanceof CubiqTS.OpsExcRecordImpl &&
        params.data.info.opsException.descriptionTxt != null &&
        params.data.info.opsException.descriptionTxt instanceof CubiqTS.ErrorsImpl
      ) {
        if (OpsIntEventPrivatizer.hasPending(params.data)) {
          params.data = OpsIntEventPrivatizer.complete(params.data);
        }

        return params.data.info.opsException.descriptionTxt;
      }
    }.bind(this);
    let descriptionFormatterFn = function (this: OpsIntTableDefinitionFactory, params) {
      let descriptionTxt: string = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.info != null &&
        params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        params.data.info.opsException != null &&
        params.data.info.opsException instanceof CubiqTS.OpsExcRecordImpl &&
        params.data.info.opsException.descriptionTxt != null &&
        params.data.info.opsException.descriptionTxt instanceof CubiqTS.ErrorsImpl
      ) {
        descriptionTxt = this.diReasonHelper.retrieveData(
          params.data.info.opsException,
          "descriptionTxt",
          true,
        );
      }

      return descriptionTxt;
    }.bind(this);
    descriptionColumn.indexType = IndexType.AgGridDefault;
    descriptionColumn.comparator = function (
      this: OpsIntTableDefinitionFactory,
      valueA,
      valueB,
      nodeA,
      nodeB,
    ) {
      let formattedA = valueA != null ? descriptionFormatterFn(nodeA) : "";
      let formattedB = valueB != null ? descriptionFormatterFn(nodeB) : "";

      if (formattedA == null) {
        formattedA = "";
      }
      if (formattedB == null) {
        formattedB = "";
      }

      return formattedA.localeCompare(formattedB);
    }.bind(this);
    descriptionColumn.valueFormatter = descriptionFormatterFn;
    let descriptionTooltipFn = function (this: OpsIntTableDefinitionFactory, params) {
      return this.diReasonHelper.retrieveTooltip(params);
    }.bind(this);
    descriptionColumn.getQuickFilterText = descriptionTooltipFn;
    descriptionColumn.tooltipValueGetter = descriptionTooltipFn;

    if (!this.restoreFromConfig(descriptionColumn)) {
      descriptionColumn.position = this.position.next();
    }

    td.addColumn(descriptionColumn);

    let reasonColumn: BfxColDef = this.getDefaultColumnDef();
    reasonColumn.field = "reasonTxt";
    reasonColumn.sortingOrder = ["asc", "desc"];
    reasonColumn.headerName = "Reason";
    reasonColumn.headerCheckboxSelection = false;
    reasonColumn.columnFilterType = ColumnFilterType.NONE;
    reasonColumn.minWidth = this.oneWs;
    reasonColumn.width = this.wrapEllipsisWs;

    reasonColumn.valueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      let reasonTxt: CubiqTS.Errors = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.info != null &&
        params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        params.data.info.opsException != null &&
        params.data.info.opsException instanceof CubiqTS.OpsExcRecordImpl &&
        params.data.info.opsException.reasonTxt != null &&
        params.data.info.opsException.reasonTxt instanceof CubiqTS.ErrorsImpl
      ) {
        if (OpsIntEventPrivatizer.hasPending(params.data)) {
          params.data = OpsIntEventPrivatizer.complete(params.data);
        }
        reasonTxt = params.data.info.opsException.reasonTxt;
      }

      return reasonTxt;
    }.bind(this);
    let reasonFormatterFn = function (this: OpsIntTableDefinitionFactory, params) {
      let reasonTxt: string = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.info != null &&
        params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        params.data.info.opsException != null &&
        params.data.info.opsException instanceof CubiqTS.OpsExcRecordImpl &&
        params.data.info.opsException.reasonTxt != null &&
        params.data.info.opsException.reasonTxt instanceof CubiqTS.ErrorsImpl
      ) {
        reasonTxt = this.diReasonHelper.retrieveData(
          params.data.info.opsException,
          "reasonTxt",
          true,
        );
      }

      return reasonTxt;
    }.bind(this);
    reasonColumn.valueFormatter = reasonFormatterFn;
    reasonColumn.indexType = IndexType.AgGridDefault;
    reasonColumn.comparator = function (
      this: OpsIntTableDefinitionFactory,
      valueA,
      valueB,
      nodeA,
      nodeB,
    ) {
      let formattedA = valueA != null ? reasonFormatterFn(nodeA) : "";
      let formattedB = valueB != null ? reasonFormatterFn(nodeB) : "";

      if (formattedA == null) {
        formattedA = "";
      }
      if (formattedB == null) {
        formattedB = "";
      }

      return formattedA.localeCompare(formattedB);
    }.bind(this);
    let reasonTooltipFn = function (this: OpsIntTableDefinitionFactory, params) {
      return this.diReasonHelper.retrieveTooltip(params);
    }.bind(this);
    reasonColumn.getQuickFilterText = reasonTooltipFn;
    reasonColumn.tooltipValueGetter = reasonTooltipFn;

    if (!this.restoreFromConfig(reasonColumn)) {
      reasonColumn.position = this.position.next();
    }

    td.addColumn(reasonColumn);

    let transactionDataColumn: BfxColDef = this.getDefaultColumnDef();
    transactionDataColumn.field = "transactionData";
    transactionDataColumn.sortingOrder = ["asc", "desc"];
    transactionDataColumn.headerName = "Transaction Data";
    transactionDataColumn.headerCheckboxSelection = false;
    transactionDataColumn.columnFilterType = ColumnFilterType.NONE;
    transactionDataColumn.minWidth = this.oneWs;
    transactionDataColumn.width = this.twentyFiveWs;
    // no maxWidth pending multi-line / autoHeight
    transactionDataColumn.valueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      let transactionData: CubiqTS.Errors = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.info != null &&
        params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        params.data.info.transactionData != null &&
        params.data.info.transactionData instanceof CubiqTS.ErrorsImpl
      ) {
        if (OpsIntEventPrivatizer.hasPending(params.data)) {
          params.data = OpsIntEventPrivatizer.complete(params.data);
        }
        transactionData = params.data.info.transactionData;
      }

      return transactionData;
    }.bind(this);
    let transactionDataFormatterFn = function (this: OpsIntTableDefinitionFactory, params) {
      let reasonTxt: string = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.info != null &&
        params.data.info instanceof CubiqTS.OpsExcInfoImpl &&
        params.data.info.transactionData != null &&
        params.data.info.transactionData instanceof CubiqTS.ErrorsImpl
      ) {
        reasonTxt = this.diReasonHelper.retrieveData(params.data.info, "transactionData", true);
      }

      return reasonTxt;
    }.bind(this);
    transactionDataColumn.valueFormatter = transactionDataFormatterFn;
    transactionDataColumn.indexType = IndexType.AgGridDefault;
    transactionDataColumn.comparator = function (
      this: OpsIntTableDefinitionFactory,
      valueA,
      valueB,
      nodeA,
      nodeB,
    ) {
      let formattedA = valueA != null ? transactionDataFormatterFn(nodeA) : "";
      let formattedB = valueB != null ? transactionDataFormatterFn(nodeB) : "";

      if (formattedA == null) {
        formattedA = "";
      }
      if (formattedB == null) {
        formattedB = "";
      }

      return formattedA.localeCompare(formattedB);
    }.bind(this);
    let transactionDataTooltipFn = function (this: OpsIntTableDefinitionFactory, params) {
      return this.diReasonHelper.retrieveTooltip(params);
    }.bind(this);
    transactionDataColumn.tooltipValueGetter = transactionDataTooltipFn;
    transactionDataColumn.getQuickFilterText = transactionDataTooltipFn;

    if (!this.restoreFromConfig(transactionDataColumn)) {
      transactionDataColumn.position = this.position.next();
    }

    td.addColumn(transactionDataColumn);

    let opsIdColumn: BfxColDef = this.getDefaultColumnDef();
    opsIdColumn.field = "eventId";
    opsIdColumn.sortingOrder = ["asc", "desc"];
    opsIdColumn.headerName = "Ops ID";
    opsIdColumn.headerCheckboxSelection = false;
    opsIdColumn.columnFilterType = ColumnFilterType.NONE;
    opsIdColumn.suppressSizeToFit = true;
    if (!this.restoreFromConfig(opsIdColumn)) {
      opsIdColumn.position = this.position.next();
    }
    opsIdColumn.minWidth = this.oneWs;
    opsIdColumn.width = this.eightWs;
    opsIdColumn.maxWidth = this.eightWs;
    // tooltip renderer requires this
    opsIdColumn.valueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      let opsId: string = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.eventId != null &&
        CubiqTS.Long.isLong(params.data.eventId)
      ) {
        opsId = params.data.eventId.toString();
      }

      return opsId;
    }.bind(this);
    opsIdColumn.tooltipValueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      return params.value;
    }.bind(this);
    td.addColumn(opsIdColumn);

    let opsErrorsColumn: BfxColDef = this.getDefaultColumnDef();
    opsErrorsColumn.field = "opsErrors";
    opsErrorsColumn.sortingOrder = ["asc", "desc"];
    opsErrorsColumn.headerName = "Ops Errors";
    opsErrorsColumn.headerCheckboxSelection = false;
    opsErrorsColumn.columnFilterType = ColumnFilterType.NONE;
    opsErrorsColumn.minWidth = this.oneWs;
    opsErrorsColumn.width = this.wrapEllipsisWs;

    opsErrorsColumn.valueGetter = function (this: OpsIntTableDefinitionFactory, params) {
      let opsErrors: CubiqTS.Errors = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.opsErrors != null &&
        params.data.opsErrors instanceof CubiqTS.ErrorsImpl
      ) {
        opsErrors = params.data.info.opsException.opsErrors;
      }

      return opsErrors;
    }.bind(this);
    let opsErrorsFormatterFn = function (this: OpsIntTableDefinitionFactory, params) {
      let opsErrors: string = null;

      if (
        params != null &&
        params.data != null &&
        params.data instanceof CubiqTS.OpsIntEventImpl &&
        params.data.opsErrors != null &&
        params.data.opsErrors instanceof CubiqTS.ErrorsImpl
      ) {
        opsErrors = this.diReasonHelper.retrieveData(params.data, "opsErrors", true);
      }

      return opsErrors;
    }.bind(this);
    opsErrorsColumn.valueFormatter = opsErrorsFormatterFn;
    opsErrorsColumn.indexType = IndexType.AgGridDefault;
    opsErrorsColumn.comparator = function (
      this: OpsIntTableDefinitionFactory,
      valueA,
      valueB,
      nodeA,
      nodeB,
    ) {
      let formattedA = valueA != null ? opsErrorsFormatterFn(nodeA) : "";
      let formattedB = valueB != null ? opsErrorsFormatterFn(nodeB) : "";

      if (formattedA == null) {
        formattedA = "";
      }
      if (formattedB == null) {
        formattedB = "";
      }

      return formattedA.localeCompare(formattedB);
    }.bind(this);
    let opsErrorsTooltipFn = function (this: OpsIntTableDefinitionFactory, params) {
      return this.diReasonHelper.retrieveTooltip(params);
    }.bind(this);
    opsErrorsColumn.getQuickFilterText = opsErrorsTooltipFn;
    opsErrorsColumn.tooltipValueGetter = opsErrorsTooltipFn;

    if (!this.restoreFromConfig(opsErrorsColumn)) {
      opsErrorsColumn.position = this.position.next();
    }

    td.addColumn(opsErrorsColumn);

    return td;
  }

  public getBlotterType() {
    return BlotterType.OPSINTERVENTION;
  }

  public getAssetClass() {
    return AssetClass.FIX;
  }
}
